import { gsap } from "gsap";

const hamburgerMenu = document.getElementById("hamburger-menu");
const topNavigation = document.getElementById("top-navigation");
export class TopNavigation {
  desktopBreakpoint: number;
  overlay: HTMLElement | null;
  hamburgerMenuButton: HTMLElement | null;
  searchButton: HTMLElement | null;
  mobileSearch: HTMLElement | null | undefined;
  mobileSearchClose: HTMLElement | null | undefined;
  searchVisible: boolean;
  searchInput: HTMLElement | null | undefined;
  clearSearch: HTMLElement | null | undefined;
  closeButton: HTMLElement | null;
  searchOverlay: HTMLElement | null;
  searchOverlayInput: HTMLInputElement | null;
  btnCloseSearch: HTMLElement | null;
  languageButton: HTMLButtonElement | null;
  languageDropdown: HTMLElement | null;
  navButtons: NodeListOf<HTMLButtonElement>;

  constructor() {
    this.desktopBreakpoint = 1025;
    this.searchVisible = false;
    this.searchButton = topNavigation?.querySelector(
      ".btn-search",
    ) as HTMLElement | null;
    this.searchOverlay = document.getElementById("search-overlay");
    this.searchOverlayInput = document.getElementById(
      "search-overlay-input",
    ) as HTMLInputElement;
    this.btnCloseSearch =
      (this.searchOverlay?.querySelector(
        ".btn-close-search",
      ) as HTMLElement | null) ?? null;

    this.searchInput = topNavigation?.querySelector("input#top-search");
    this.mobileSearch = topNavigation?.querySelector(".top-search");
    this.mobileSearchClose = this.mobileSearch?.querySelector(
      ".btn-close-search-mobile",
    ) as HTMLElement | null;
    this.clearSearch = this.mobileSearch?.querySelector(
      ".btn-clear-text",
    ) as HTMLElement | null;
    this.overlay = document.querySelector(".pageoverlay") as HTMLElement | null;
    this.hamburgerMenuButton = document.querySelector(
      ".btn-hamburger-menu",
    ) as HTMLElement | null;
    this.closeButton = document.querySelector(
      "#close-hamburger-menu-button",
    ) as HTMLElement | null;
    this.languageButton = document.querySelector(
      ".language-selector button",
    ) as HTMLButtonElement | null;
    this.languageDropdown = document.querySelector(
      ".language-dropdown",
    ) as HTMLElement | null;
    this.navButtons =
      topNavigation?.querySelectorAll(".nav-button") ??
      document.querySelectorAll<HTMLButtonElement>(".nav-button");

    this.languageButton?.addEventListener("click", (e: Event) => {
      e.stopPropagation();
      this.closeNavDropdowns();
      const isExpanded =
        this.languageButton?.getAttribute("aria-expanded") === "true";

      this.languageButton?.setAttribute("aria-expanded", String(!isExpanded));
      this.languageDropdown?.classList.toggle("show");
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", () => {
      this.closeLanguageDropdown();
    });

    // Close dropdown when clicking a language link
    this.languageDropdown
      ?.querySelectorAll('[role="menuitem"]')
      .forEach((link) => {
        link.addEventListener("click", () => {
          this.closeLanguageDropdown();
        });
      });

    // Close language dropdown on ESC
    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (
        e.key === "Escape" &&
        this.languageDropdown?.classList.contains("show")
      ) {
        this.closeLanguageDropdown();
        this.languageButton?.focus();
      }
    });

    // Handle all navigation dropdown buttons (main nav dropdowns)
    this.navButtons.forEach((navButton) => {
      const navDropdown = navButton.getAttribute("aria-controls")
        ? document.getElementById(navButton.getAttribute("aria-controls")!)
        : null;

      navButton.addEventListener("click", (e: Event) => {
        e.stopPropagation();
        this.closeLanguageDropdown();

        const isExpanded = navButton.getAttribute("aria-expanded") === "true";

        // Close other dropdowns
        this.navButtons.forEach((btn) => {
          if (btn !== navButton) {
            btn.setAttribute("aria-expanded", "false");
            const otherId = btn.getAttribute("aria-controls");
            if (otherId) {
              document.getElementById(otherId)?.classList.remove("show");
            }
          }
        });

        // Toggle this dropdown
        navButton.setAttribute("aria-expanded", String(!isExpanded));
        navDropdown?.classList.toggle("show");
      });
    });

    // Close nav dropdowns when clicking outside
    document.addEventListener("click", (e: Event) => {
      const target = e.target as Element;
      const isClickOnNavButton = target.closest(".nav-button");
      const isClickOnNavDropdown = target.closest(".nav-dropdown");

      if (!isClickOnNavButton && !isClickOnNavDropdown) {
        this.closeNavDropdowns();
      }
    });

    // Close nav dropdowns when clicking a menu item
    this.navButtons.forEach((navButton) => {
      const navDropdownId = navButton.getAttribute("aria-controls");
      const navDropdown = navDropdownId
        ? document.getElementById(navDropdownId)
        : null;

      navDropdown?.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", () => {
          navButton.setAttribute("aria-expanded", "false");
          navDropdown.classList.remove("show");
        });
      });
    });

    // Close nav dropdowns on ESC
    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        let focusedButton: HTMLButtonElement | undefined;

        for (const btn of this.navButtons) {
          if (btn.getAttribute("aria-expanded") === "true") {
            btn.setAttribute("aria-expanded", "false");
            const navDropdownId = btn.getAttribute("aria-controls");

            if (navDropdownId) {
              document.getElementById(navDropdownId)?.classList.remove("show");
            }

            focusedButton = btn;
          }
        }

        if (focusedButton) {
          focusedButton.focus();
        }
      }
    });

    if (this.searchButton) {
      this.searchButton.addEventListener("click", () => {
        this.toggleSearchOverlay();
      });
    }

    if (this.btnCloseSearch) {
      this.btnCloseSearch.addEventListener("click", () =>
        this.closeSearchOverlay(),
      );
    }

    if (this.searchOverlay) {
      this.searchOverlay.addEventListener("click", (e: Event) => {
        if (e.target === this.searchOverlay) {
          this.closeSearchOverlay();
        }
      });
    }

    this.mobileSearchClose?.addEventListener("click", () => this.closeSearch());
    this.hamburgerMenuButton?.addEventListener("click", () =>
      this.openHamburgerMenu(),
    );
    this.closeButton?.addEventListener("click", () =>
      this.closeHamburgerMenu(),
    );
    this.overlay?.addEventListener("click", () => this.closeHamburgerMenu());
    this.clearSearch?.addEventListener("click", () =>
      this.searchInput?.focus(),
    );
    window.addEventListener("resize", () => this.handleResize());

    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        if (this.searchOverlay && !this.searchOverlay.hasAttribute("hidden")) {
          this.trapFocus(e, this.searchOverlay);
          return;
        }

        if (
          hamburgerMenu instanceof HTMLElement &&
          hamburgerMenu.classList.contains("open")
        ) {
          this.trapFocus(e, hamburgerMenu);
        }
      }

      if (e.key === "Escape") {
        if (hamburgerMenu?.classList.contains("open")) {
          this.closeHamburgerMenu();
        }
        if (!this.searchOverlay?.hasAttribute("hidden")) {
          this.closeSearchOverlay();
        }
        if (this.searchVisible) {
          this.closeSearch();
        }
      }
    });

    this.initHamburgerChevrons();
    this.removeTabbingTopSearch();
    this.handleResize();
  }

  closeLanguageDropdown() {
    this.languageButton?.setAttribute("aria-expanded", "false");
    this.languageDropdown?.classList.remove("show");
  }

  closeNavDropdowns() {
    this.navButtons.forEach((btn) => {
      btn.setAttribute("aria-expanded", "false");
      const navDropdownId = btn.getAttribute("aria-controls");

      if (navDropdownId) {
        document.getElementById(navDropdownId)?.classList.remove("show");
      }
    });
  }

  handleResize() {
    if (window.innerWidth >= this.desktopBreakpoint) {
      hamburgerMenu?.classList.remove("open", "close");
      (this.hamburgerMenuButton as HTMLElement | null)?.setAttribute(
        "aria-expanded",
        "false",
      );
      gsap.set(this.overlay, {
        autoAlpha: 0,
      });
      gsap.set(this.hamburgerMenuButton, {
        opacity: 1,
      });

      if (hamburgerMenu) {
        this.inActivateTabFunctionality(hamburgerMenu as HTMLElement);
      }

      this.closeSearchOverlay(false);
    }
  }

  getFocusableElements(container: HTMLElement) {
    return Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => {
      if (element.hasAttribute("hidden")) {
        return false;
      }

      if (element.closest("[hidden]")) {
        return false;
      }

      return element.offsetParent !== null;
    });
  }

  trapFocus(e: KeyboardEvent, container: HTMLElement) {
    const focusableElements = this.getFocusableElements(container);

    if (focusableElements.length === 0) {
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (!activeElement || !container.contains(activeElement)) {
      e.preventDefault();
      firstFocusable.focus();
      return;
    }

    if (e.shiftKey && activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
    } else if (!e.shiftKey && activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  }

  initHamburgerChevrons() {
    const hamburgerMenuChevrons = hamburgerMenu?.querySelectorAll(
      ".chevron",
    ) as unknown as HTMLLabelElement[];

    hamburgerMenuChevrons.forEach((element) => {
      element.addEventListener("click", () => {
        const checkbox = document.getElementById(`list-item-${element.id}`);
        const subMenu = document.getElementById(`sub-menu-${element.id}`);

        if (!checkbox) return;
        const checkboxHTMLElement = checkbox as HTMLInputElement;

        if (subMenu) {
          if (checkboxHTMLElement.checked) {
            this.closeMenuItem(subMenu);
          } else {
            this.expandMenuItem(subMenu);
          }
        }
      });

      element.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          const checkbox = document.getElementById(`list-item-${element.id}`);
          const subMenu = document.getElementById(`sub-menu-${element.id}`);

          if (!checkbox) return;
          const checkboxHTMLElement = checkbox as HTMLInputElement;

          checkboxHTMLElement.checked = !checkboxHTMLElement.checked;

          if (subMenu) {
            if (!checkboxHTMLElement.checked) {
              this.closeMenuItem(subMenu);
            } else {
              this.expandMenuItem(subMenu);
            }
          }
        }
      });
    });
  }

  removeTabbingTopSearch() {
    this.mobileSearch
      ?.querySelector("#top-search")
      ?.setAttribute("tabindex", "-1");
    this.mobileSearch
      ?.querySelector(".btn-clear-text")
      ?.setAttribute("tabindex", "-1");
    this.mobileSearch
      ?.querySelector(".submit-search")
      ?.setAttribute("tabindex", "-1");
    this.mobileSearch
      ?.querySelector(".btn-close-search-mobile")
      ?.setAttribute("tabindex", "-1");
  }

  addTabbingTopSearch() {
    this.mobileSearch
      ?.querySelector("#top-search")
      ?.setAttribute("tabindex", "0");
    this.mobileSearch
      ?.querySelector(".btn-clear-text")
      ?.setAttribute("tabindex", "0");
    this.mobileSearch
      ?.querySelector(".submit-search")
      ?.setAttribute("tabindex", "0");
    this.mobileSearch
      ?.querySelector(".btn-close-search-mobile")
      ?.setAttribute("tabindex", "0");
  }

  toggleSearch() {
    if (this.searchVisible) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    topNavigation?.classList.add("search-visible");
    this.mobileSearch?.classList.add("show");
    this.searchButton?.classList.add("active");
    this.searchInput?.focus();
    this.searchVisible = true;

    this.addTabbingTopSearch();
  }

  closeSearch() {
    topNavigation?.classList.remove("search-visible");
    this.mobileSearch?.classList.remove("show");
    this.searchButton?.classList.remove("active");
    this.searchVisible = false;

    this.removeTabbingTopSearch();
  }

  openHamburgerMenu() {
    this.closeLanguageDropdown();
    this.closeNavDropdowns();
    (this.hamburgerMenuButton as HTMLElement | null)?.setAttribute(
      "aria-expanded",
      "true",
    );

    const path = window.location.pathname;
    const pagePathTree = path.substring(1, path.length - 1).split("/");

    const navLink = document.getElementById(
      `nav-link-${pagePathTree[pagePathTree.length - 1].toLowerCase()}`,
    );
    navLink?.classList.add("selected");
    pagePathTree.forEach((path, index) => {
      const menu = document.getElementById(`sub-menu-${path.toLowerCase()}`);
      const checkbox = document.getElementById(
        `list-item-${path.toLowerCase()}`,
      ) as HTMLInputElement;

      if (menu && checkbox && index != pagePathTree.length - 1) {
        checkbox.checked = true;
        this.expandMenuItem(menu);
      }
    });

    if (hamburgerMenu) {
      setTimeout(() => {
        const firstFocusable = this.getFocusableElements(hamburgerMenu)[0];
        firstFocusable?.focus();
      }, 400);

      this.activateTabFunctionality(hamburgerMenu as HTMLElement);
    }

    gsap.to(this.hamburgerMenuButton, {
      opacity: 0,
      duration: 0.1,
      onComplete: () => {
        hamburgerMenu?.classList.remove("close");
        hamburgerMenu?.classList.add("open");

        gsap.to(this.overlay, {
          autoAlpha: 0.5,
          duration: 0.1,
        });
      },
    });
  }

  closeHamburgerMenu() {
    hamburgerMenu?.classList.remove("open");
    hamburgerMenu?.classList.add("close");
    (this.hamburgerMenuButton as HTMLElement | null)?.setAttribute(
      "aria-expanded",
      "false",
    );
    gsap.to(this.overlay, {
      autoAlpha: 0,
      duration: 0.3,
    });
    gsap.to(this.hamburgerMenuButton, {
      opacity: 1,
      duration: 0.1,
      delay: 0.2,
    });

    this.inActivateTabFunctionality(hamburgerMenu as HTMLElement);
  }

  expandMenuItem(item: Element) {
    const expandDuration = 0.3;

    gsap.to(item, {
      height: "auto",
      duration: expandDuration,
      delay: 0,
    });

    this.activateTabFunctionality(item as HTMLElement);
  }

  closeMenuItem(item: Element) {
    const subMenuHeight = 0;
    gsap.to(item, {
      height: subMenuHeight,
      duration: 0.2,
      delay: 0,
    });

    this.inActivateTabFunctionality(item as HTMLElement);
  }

  activateTabFunctionality(item: HTMLElement) {
    const list = item.querySelector("ul");
    const itemElements = list?.querySelectorAll(":scope > .nav-item");

    itemElements?.forEach((element) => {
      const chevron = element.querySelector(
        ".chevron",
      ) as unknown as HTMLElement;
      const link = element.querySelector(".nav-link") as unknown as HTMLElement;
      if (chevron?.dataset?.tabIndex) {
        chevron.setAttribute("tabindex", "1");
      }

      if (link?.dataset?.tabIndex) {
        link.setAttribute("tabindex", "1");
      }
    });
  }

  inActivateTabFunctionality(item: HTMLElement) {
    const chevrons = item.querySelectorAll(
      ".chevron",
    ) as unknown as HTMLElement[];
    const links = item.querySelectorAll(
      ".nav-link",
    ) as unknown as HTMLElement[];

    chevrons.forEach((chevron) => {
      chevron.setAttribute("tabindex", "-1");
    });

    links.forEach((link) => {
      link.setAttribute("tabindex", "-1");
    });
  }

  toggleSearchOverlay() {
    if (this.searchOverlay?.hasAttribute("hidden")) {
      this.openSearchOverlay();
    } else {
      this.closeSearchOverlay();
    }
  }

  openSearchOverlay() {
    this.searchOverlay?.removeAttribute("hidden");
    this.searchButton?.setAttribute("aria-expanded", "true");
    if (this.searchOverlayInput) {
      this.searchOverlayInput.focus();
    }
    document.body.style.overflow = "hidden";
  }

  closeSearchOverlay(restoreFocus = true) {
    this.searchOverlay?.setAttribute("hidden", "");
    this.searchButton?.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
    if (restoreFocus && this.searchButton) {
      this.searchButton.focus();
    }
  }
}
